import React, { useCallback, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Clock, AlertCircle } from 'lucide-react';
import JobHeader from '../components/JobHeader';
import ResultsSummary from '../components/ResultsSummary';
import FirstTimeGuidance from '../components/FirstTimeGuidance';
import PhotoResultCard from '../components/PhotoResultCard';
import BatchActionsBar from '../components/job-results/BatchActionsBar';
import useJobResultsData from '../hooks/useJobResultsData';
import usePhotoSelection from '../hooks/usePhotoSelection';
import usePhotoMetadata from '../hooks/usePhotoMetadata';
import { PhotoMetadataProvider } from '../contexts/PhotoMetadataContext';

const JobResults = () => {
  const { jobId } = useParams();
  const photoGridRef = useRef(null);
  const [showFirstTimeHelp, setShowFirstTimeHelp] = useState(true);
  const [batchApplying, setBatchApplying] = useState(false);

  const handleServerBatchApplyDetected = useCallback(() => {
    setBatchApplying(false);
  }, []);

  const {
    selectedPhotos,
    expandedCards,
    approvedPhotos,
    setExpandedPhotoCards,
    togglePhotoSelection,
    toggleCardExpansion,
    approvePhoto,
    rejectPhoto,
    autoSelectPhotos,
    deselectAllPhotos,
    bulkApproveAll
  } = usePhotoSelection(jobId);

  const {
    job,
    setJob,
    loading,
    isPolling,
    startPolling,
    provider,
    providerName,
    isProcessing
  } = useJobResultsData(jobId, {
    onAutoSelect: autoSelectPhotos,
    isClientBatchApplying: batchApplying,
    onServerBatchApplyDetected: handleServerBatchApplyDetected
  });

  const {
    editingField,
    editingValue,
    setEditingValue,
    savingState,
    applyingState,
    copiedStates,
    removingTags,
    copyToClipboard,
    startEditing,
    cancelEditing,
    saveEdit,
    handleKeyPress,
    restoreOriginal,
    removeTag,
    applyPhotoMetadata,
    undoApply,
    batchApplyMetadata
  } = usePhotoMetadata({
    jobId,
    job,
    setJob,
    providerName,
    selectedPhotos,
    clearSelection: deselectAllPhotos,
    ensurePolling: startPolling,
    isPolling,
    setBatchApplying
  });

  const getProgressStats = useCallback(() => {
    if (!job?.results) {
      return { approved: 0, total: 0, remaining: 0 };
    }

    const processedResults = job.results.filter((result) => result.status === 'processed');
    const approved = approvedPhotos.size;
    const total = processedResults.length;
    const remaining = total - approved;

    return { approved, total, remaining };
  }, [approvedPhotos, job]);

  const bulkApproveAllAndApply = useCallback(async () => {
    const photoIds = bulkApproveAll(job);
    if (photoIds.length === 0) {
      return;
    }

    await batchApplyMetadata(photoIds);
  }, [batchApplyMetadata, bulkApproveAll, job]);

  const scrollToFirstPhoto = useCallback(() => {
    setShowFirstTimeHelp(false);

    setTimeout(() => {
      if (photoGridRef.current) {
        photoGridRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });

        if (job?.results?.length) {
          const firstProcessedPhoto = job.results.find((result) => result.status === 'processed');
          if (firstProcessedPhoto) {
            setExpandedPhotoCards([firstProcessedPhoto.photoId]);
          }
        }
      }
    }, 100);
  }, [job, setExpandedPhotoCards]);

  if (loading) {
    return (
      <div className="text-center py-12">
        <Clock className="mx-auto h-12 w-12 text-gray-400 mb-4 animate-spin" />
        <p className="text-gray-600">Loading job details...</p>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="mx-auto h-12 w-12 text-red-400 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Job Not Found</h3>
        <p className="text-gray-600 mb-4">The requested job could not be found.</p>
        <Link
          to="/history"
          className="inline-flex items-center text-blue-600 hover:text-blue-800"
        >
          <ArrowLeft className="mr-1 h-4 w-4" />
          Back to Job History
        </Link>
      </div>
    );
  }

  const isApplying = batchApplying || job.applyProgress?.isApplying;

  return (
    <div className="max-w-7xl mx-auto">
      <JobHeader job={job} isProcessing={isProcessing} batchApplying={batchApplying} />

      {job.results && job.results.length > 0 ? (
        <div>
          <ResultsSummary
            job={job}
            getProgressStats={getProgressStats}
            bulkApproveAll={() => bulkApproveAll(job)}
            bulkApproveAllAndApply={bulkApproveAllAndApply}
            batchApplyMetadata={batchApplyMetadata}
            batchApplying={batchApplying}
            setShowFirstTimeHelp={setShowFirstTimeHelp}
            scrollToFirstPhoto={scrollToFirstPhoto}
            selectedPhotos={selectedPhotos}
            provider={provider}
            providerName={providerName}
          />

          <FirstTimeGuidance
            showFirstTimeHelp={showFirstTimeHelp}
            setShowFirstTimeHelp={setShowFirstTimeHelp}
            hasProcessedResults={job.results.some((result) => result.status === 'processed')}
            providerName={providerName}
          />

          <PhotoMetadataProvider
            value={{
              editingField,
              editingValue,
              setEditingValue,
              savingState,
              applyingState,
              copiedStates,
              removingTags,
              copyToClipboard,
              startEditing,
              cancelEditing,
              saveEdit,
              handleKeyPress,
              restoreOriginal,
              removeTag,
              applyPhotoMetadata,
              undoApply
            }}
          >
            <div
              ref={photoGridRef}
              className={`grid grid-cols-1 gap-4 ${selectedPhotos.size > 0 ? 'pb-24' : ''}`}
            >
              {job.results.map((result) => (
                <PhotoResultCard
                  key={result.photoId}
                  result={result}
                  selectedPhotos={selectedPhotos}
                  expandedCards={expandedCards}
                  approvedPhotos={approvedPhotos}
                  batchApplying={batchApplying}
                  job={job}
                  togglePhotoSelection={togglePhotoSelection}
                  toggleCardExpansion={toggleCardExpansion}
                  approvePhoto={approvePhoto}
                  rejectPhoto={rejectPhoto}
                />
              ))}
            </div>
          </PhotoMetadataProvider>
        </div>
      ) : (
        <div className="text-center py-12">
          <AlertCircle className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Results</h3>
          <p className="text-gray-600">No photos found for this job.</p>
        </div>
      )}

      <BatchActionsBar
        selectedPhotos={selectedPhotos}
        jobResults={job.results}
        providerName={providerName}
        onClearSelection={deselectAllPhotos}
        onApply={() => batchApplyMetadata()}
        isApplying={isApplying}
      />
    </div>
  );
};

export default JobResults;
